import React from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useAppSelector } from 'flow/hooks/useRedux';
import {
    selectCallRecordContact,
    selectCallRecordPhoneNumber,
    selectCallRecordQueueKey,
} from 'flow/selectors/call-records-selectors';
import { CallRecordKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { generatePath } from 'flow/utils/router-utils';
import ContactModalShell from './ContactModalShell';

interface Props {
    callRecordKey?: CallRecordKey;
}

type CallRecordData = {
    callRecordKey: CallRecordKey;
    contactKey?: string;
    queueKey?: string;
    number?: string;
};

/**
 * The calendar next to this call record in the section the modal is open in,
 * so the user stays in Calls or in the PBX queue. The keys come from the call
 * record; only the section is read from the page. Elsewhere (the central
 * switch, AI agent calls) there is no such route, and the card falls back to
 * the colleague's calendar under Contacts.
 */
export const getCallRecordCalendarPath = (
    pathname: string,
    { callRecordKey, contactKey, queueKey, number }: CallRecordData,
): string | undefined => {
    if (!contactKey) {
        return undefined;
    }

    if (matchPath(pathname, { path: routes['calls'] })) {
        return generatePath(routes['calls.callRecordKey.contactKey.calendar'], {
            callRecordKey,
            contactKey,
        });
    }

    if (
        queueKey &&
        number &&
        matchPath(pathname, { path: routes['pbx.queues.entityKey'] })
    ) {
        return generatePath(
            routes[
                'pbx.queues.entityKey.number.callRecordKey.contactKey.calendar'
            ],
            { entityKey: queueKey, bPart: number, callRecordKey, contactKey },
        );
    }

    return undefined;
};

const CallRecordContactModal = ({ callRecordKey }: Props) => {
    const { pathname } = useLocation();

    const contact = useAppSelector((state) =>
        callRecordKey
            ? selectCallRecordContact(state, callRecordKey)
            : undefined,
    );
    const queueKey = useAppSelector((state) =>
        callRecordKey
            ? selectCallRecordQueueKey(state, callRecordKey)
            : undefined,
    );
    const number = useAppSelector((state) =>
        callRecordKey
            ? selectCallRecordPhoneNumber(state, callRecordKey)
            : undefined,
    );

    const calendarPath = callRecordKey
        ? getCallRecordCalendarPath(pathname, {
              callRecordKey,
              contactKey: contact?.get('key'),
              queueKey,
              number,
          })
        : undefined;

    return <ContactModalShell contact={contact} calendarPath={calendarPath} />;
};

export default CallRecordContactModal;
